// Maximally observable state reconstruction.
//
// A trace is a log of what an agent looked at and what it changed. Where those two
// address the same thing by a stable name, you can fold them into a most-recent
// known state — the same way reads and edits over a file path stack into an
// accumulated file, and for the same reason: the operations are addressed, ordered,
// and reported.
//
// The generalization worth having is not the folding, which is easy. It is knowing
// WHICH PART of an observation you are allowed to accumulate. An observation carries
// two kinds of content mixed together:
//
//   the schema of the SYSTEM the agent talked to — a kubectl table, an HTTP status,
//     a SQL result set, a package manifest. Defined by the platform, identical
//     across every tenant, parseable with no knowledge of the specific service.
//
//   the schema of the SERVICE ITSELF — what "partition 29: low=4139 high=8264"
//     means. Defined by whoever wrote that service, different everywhere, and
//     unknowable without domain knowledge.
//
// Accumulate the first. Never build a classifier for the second: it is the
// mechanical-proxy-for-meaning failure with extra steps, and it will silently
// mis-report the moment a service changes its output format. Carry the second
// through as evidence for a reader who knows the service, or let a model annotate
// it at render time.
//
// THE READING THIS EXISTS FOR is not the state itself. Trace-derived state is
// sparse and biased by construction — you know the state of exactly the things
// someone was debugging, at exactly the moments they looked, which is not
// monitoring and should never be presented as it. What it IS: a record of what was
// known and when. And the sharpest question that record answers is whether a change
// was ever CHECKED — an agent that scales a service and never reads it again has
// concluded something it did not observe.

const tsOf = (v) => {
  if (v == null) return null;
  if (typeof v === "number") return v;
  const t = Date.parse(String(v).replace(" ", "T"));
  return Number.isNaN(t) ? null : t;
};

/**
 * Fold observations and changes over named targets into last-known state.
 *
 * @param {object[]} ops   in any order; sorted internally by `at`
 * @param {object} spec
 *   target (op) => string|null   what this op addresses. Null ops are skipped —
 *                                an unaddressed observation cannot accumulate.
 *   at     (op) => timestamp
 *   read   (op) => object|null   the PLATFORM-schema facts this op observed.
 *                                Return null for anything you would have to
 *                                interpret; that is a different layer.
 *   change (op) => string|null   a short description of a mutation this op made.
 *   merge  (prev, next) => object  optional; defaults to a shallow field-wise
 *                                overlay where a later non-null value wins, so a
 *                                narrow observation does not erase a wider one.
 */
export function reconstructState(ops, { target, at, read, change, merge } = {}) {
  if (typeof target !== "function") throw new TypeError("reconstructState needs target(op)");
  if (typeof at !== "function") throw new TypeError("reconstructState needs at(op)");

  const ordered = [...(ops || [])]
    .map((op) => ({ op, t: tsOf(at(op)) }))
    .sort((a, b) => (a.t ?? 0) - (b.t ?? 0));

  const byTarget = new Map();
  for (const { op, t } of ordered) {
    const key = target(op);
    if (!key) continue;
    let s = byTarget.get(key);
    if (!s) {
      s = { target: key, state: null, observedAt: null, observations: 0, changes: [], reads: [] };
      byTarget.set(key, s);
    }

    const observed = read ? read(op) : null;
    if (observed && Object.keys(observed).length) {
      s.state = s.state ? (merge ? merge(s.state, observed) : overlay(s.state, observed)) : { ...observed };
      s.observedAt = t;
      s.observations += 1;
      s.reads.push({ at: t, state: { ...observed }, key: op.key });
      // A change is CHECKED once any later observation lands. This deliberately
      // claims no causation — only that the agent looked again.
      for (const c of s.changes) {
        if (c.checked) continue;
        c.checked = true;
        c.checkedAt = t;
        // Reporting whether the state differs is honest; asserting the change
        // caused the difference is not, and nothing here does.
        c.stateDiffered = c.stateBefore ? differs(c.stateBefore, observed) : null;
      }
    }

    const changed = change ? change(op) : null;
    if (changed) {
      s.changes.push({
        what: changed, at: t, key: op.key,
        stateBefore: s.state ? { ...s.state } : null,
        checked: false, checkedAt: null, stateDiffered: null,
      });
    }
  }

  const targets = [...byTarget.values()].map((s) => ({
    ...s,
    // The finding, named: a change nobody looked at afterwards.
    unchecked: s.changes.filter((c) => !c.checked).length,
  }));

  return {
    targets: targets.sort((a, b) => (b.observedAt ?? 0) - (a.observedAt ?? 0)),
    totals: {
      targets: targets.length,
      observed: targets.filter((t) => t.state).length,
      changes: targets.reduce((n, t) => n + t.changes.length, 0),
      unchecked: targets.reduce((n, t) => n + t.unchecked, 0),
    },
  };
}

/** A later non-null field wins; a narrow observation never erases a wider one. */
function overlay(prev, next) {
  const out = { ...prev };
  for (const [k, v] of Object.entries(next)) if (v != null && v !== "") out[k] = v;
  return out;
}

function differs(a, b) {
  for (const [k, v] of Object.entries(b || {})) if (v != null && a?.[k] !== v) return true;
  return false;
}

/**
 * A one-line rendering of last-known state, for a panel.
 *
 * Always carries WHEN, because trace-derived state without its timestamp reads as
 * current fact when it is a historical observation.
 */
export function describeState(entry, { fields } = {}) {
  if (!entry?.state) return null;
  const keys = fields || Object.keys(entry.state);
  const body = keys.map((k) => (entry.state[k] == null ? null : `${k} ${entry.state[k]}`)).filter(Boolean).join(" · ");
  if (!body) return null;
  const when = entry.observedAt ? new Date(entry.observedAt).toISOString().replace("T", " ").slice(0, 16) : "unknown time";
  return `${body} — as last observed ${when}`;
}
